import { DatePickerInput, Modal } from '@sequencehq/core-components'
import { Footer } from '../common/Footer'
import { format } from '@sequencehq/utils/dates'
import { Box, Grid } from '@chakra-ui/react'
import { GreyGrey80, Lato13Bold } from '@sequencehq/design-tokens'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/validation'
import { RadioGroup } from '../../../../common/RadioGroup'
import { useAtom } from 'jotai'
import { modalScreenAtom } from '../../atoms/screens.atom'
import { cancelDateSelectionAtom, CancelDateType } from './cancel.atom'
import { useEndOfCurrentBillingPeriod } from '../../utils/useEndOfNextBillingPeriod'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { DATE_FORMAT } from '../../editModalShortcuts.constants'

type FormData = {
  cancelDateType: CancelDateType
  customDate?: Date
}

const useCancelScreen = () => {
  const [, setModalScreen] = useAtom(modalScreenAtom)
  const [cancelDateSelection, setDateCancelSelection] = useAtom(
    cancelDateSelectionAtom
  )
  const billingScheduleId = useBillingScheduleId()
  const endOfCurrentBillingPeriodQuery = useEndOfCurrentBillingPeriod({
    billingScheduleId
  })

  const form = useForm<FormData>({
    value: cancelDateSelection,
    fieldConfiguration: [
      {
        property: 'cancelDateType',
        validation: [required]
      },
      {
        property: 'customDate',
        disabled: ctx => ctx.formData.cancelDateType !== 'customDate',
        validation: [required]
      }
    ]
  })

  const onSubmit = () => {
    setDateCancelSelection(form.queries.formData)
    setModalScreen('cancel-confirm')
  }

  return {
    isLoading: endOfCurrentBillingPeriodQuery.isLoading,
    endOfBillingPeriod: endOfCurrentBillingPeriodQuery.endOfBillingPeriod,
    formFields: form.fields,
    isValid: form.queries.isValid,
    onSubmit
  }
}

export const CancelScreen = () => {
  const { isLoading, endOfBillingPeriod, formFields, isValid, onSubmit } =
    useCancelScreen()

  return (
    <>
      <Modal.Content>
        <Grid gridTemplateRows="auto auto auto" gap="12px">
          <Box {...Lato13Bold} color={GreyGrey80}>
            When should changes take effect?
          </Box>
          <RadioGroup
            name="cancelDateType"
            onChange={newValue =>
              formFields.cancelDateType.onChange(
                newValue as FormData['cancelDateType']
              )
            }
            value={formFields.cancelDateType.value}
            options={[
              {
                label: 'Immediately',
                description: 'Cancel schedule immediately.',
                labelAnnotation:
                  formFields.cancelDateType.value === 'immediate'
                    ? format(new Date(), DATE_FORMAT)
                    : '',
                value: 'immediate'
              },
              {
                label: 'End of current billing period',
                description:
                  'Complete schedule at the end of the current billing period',
                labelAnnotation:
                  formFields.cancelDateType.value === 'endOfBillingPeriod' &&
                  endOfBillingPeriod
                    ? format(endOfBillingPeriod, DATE_FORMAT)
                    : '',
                value: 'endOfBillingPeriod',
                disabled: !endOfBillingPeriod || isLoading,
                disabledReason:
                  !endOfBillingPeriod || isLoading
                    ? 'Could not calculate next billing date'
                    : undefined
              },
              {
                label: 'Custom',
                description: 'Complete the schedule on a customer date.',
                value: 'customDate',
                additionalComponent:
                  formFields.cancelDateType.value === 'customDate' ? (
                    <Box paddingTop="12px">
                      <DatePickerInput
                        minDate={new Date()}
                        width="180px"
                        value={formFields.customDate.value}
                        onChange={newDate =>
                          formFields.customDate.onChange(newDate)
                        }
                      />
                    </Box>
                  ) : null
              }
            ]}
          />
        </Grid>
      </Modal.Content>
      <Footer onContinue={onSubmit} disabled={!isValid} />
    </>
  )
}
